import { createContext, useContext, useState, useEffect } from 'react'
import * as api from '../services/api.js'

const AuthContext = createContext(null)

function setUserRoleCookie(role) {
  try {
    document.cookie = `user_role=${role};path=/;max-age=${role ? 86400 : 0};SameSite=Lax`
  } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setUserRoleCookie(user?.role || '')
  }, [user])

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const stored = localStorage.getItem('user')
    if (stored) {
      try {
        const u = JSON.parse(stored)
        setUser(u)
        setUserRoleCookie(u?.role || '')
      } catch {}
    }
    setLoading(false)

    const syncUser = () => {
      const s = localStorage.getItem('user')
      if (s) {
        try { setUser(JSON.parse(s)) } catch { setUser(null) }
      } else {
        setUser(null)
      }
    }

    window.addEventListener('auth-change', syncUser)
    window.addEventListener('storage', syncUser)
    return () => {
      window.removeEventListener('auth-change', syncUser)
      window.removeEventListener('storage', syncUser)
    }
  }, [])

  const login = async (username, password, code, challengeToken, _ts) => {
    try {
      const userData = await api.login(username, password, code, challengeToken, _ts)
      setUser(userData)
      localStorage.setItem('user', JSON.stringify(userData))
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const logout = async () => {
    localStorage.removeItem('user')
    setUser(null)
  }

  const loginWithGoogle = async (credential) => {
    try {
      const userData = await api.googleLogin(credential)
      setUser(userData)
      localStorage.setItem('user', JSON.stringify(userData))
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const register = async (username, password, email, code, challengeToken, _ts, newsletterSubscribed = false) => {
    try {
      await api.register(username, password, email, code, challengeToken, _ts, newsletterSubscribed)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    register,
    isAuthenticated: !!user,
    isAuthLoading: loading,
    isAdmin: user?.role === 'admin',
    isEditor: user?.role === 'admin' || user?.role === 'editor',
    role: user?.role || 'user',
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
